/**
 * Utility functions for SearchableList component
 */

/**
 * Escape special regex characters for safe text search
 */
export const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Highlight text matches in HTML content
 */
export const highlightText = (html: string, query: string): string => {
  if (!query.trim()) return html

  // Create a temporary div to work with HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Function to highlight text in text nodes
  const highlightTextNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || ''
      const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi')

      if (regex.test(text)) {
        const span = document.createElement('span')
        span.innerHTML = text.replace(regex, '<span class="highlight">$1</span>')
        node.parentNode?.replaceChild(span, node)
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element
      if (!/script|style|textarea/i.test(element.tagName)) {
        // Clone children array to avoid issues with live NodeList
        const children = Array.from(element.childNodes)
        children.forEach((child) => highlightTextNode(child))
      }
    }
  }

  highlightTextNode(tempDiv)
  return tempDiv.innerHTML
}

/**
 * Remove highlights from HTML content
 */
export const removeHighlights = (html: string): string => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Remove all highlight spans
  const highlights = tempDiv.querySelectorAll('.highlight')
  highlights.forEach((highlight) => {
    const parent = highlight.parentNode
    if (parent) {
      parent.insertBefore(document.createTextNode(highlight.textContent || ''), highlight)
      parent.removeChild(highlight)
    }
  })

  return tempDiv.innerHTML
}

/**
 * Search through text content of an element
 */
export const searchInElement = (element: Element, query: string): boolean => {
  const searchText = element.textContent?.toLowerCase() || ''
  const queryLower = query.toLowerCase()
  return searchText.includes(queryLower)
}

/**
 * Debounce function to limit the rate of function calls
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    timeoutId = setTimeout(() => {
      func(...args)
    }, wait)
  }
}
